import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useFormContext } from "./FormContext";
import MenuContext from "./MenuContext";


const itemsPerPage = 5;


const WorkflowTableModal = ({ isOpen, onClose, workFlowPageData = [] }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageRows, setPageRows] = useState([]);


    const { cloneWorkflow } = useFormContext();
    const { setActiveTab } = useContext(MenuContext);
    const navigate = useNavigate();


    const totalPages = Math.ceil((workFlowPageData.length || 0) / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    // 🔹 keep a sorted copy of workflows (latest process_date on top)
    const [sortedData, setSortedData] = useState([]);

    useEffect(() => {
        // sort whenever source data changes
        const sorted = [...workFlowPageData].sort((a, b) => {
            const da = a.process_date ? new Date(a.process_date) : 0;
            const db = b.process_date ? new Date(b.process_date) : 0;
            return db - da; // latest first
        });
        setSortedData(sorted);
    }, [workFlowPageData]);

    useEffect(() => {
        if (!isOpen) return;
        const currentData =
            (sortedData || []).slice(indexOfFirstItem, indexOfLastItem) || [];
        setPageRows(currentData);
    }, [isOpen, currentPage, sortedData, indexOfFirstItem, indexOfLastItem]);


    // reset to page 1 whenever modal closes
    useEffect(() => {
        if (!isOpen) setCurrentPage(1);
    }, [isOpen]);


    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };


    const handleClone = async (elem) => {
        console.log("elem to clone:", elem);

        if (!elem) return;
        
        try {
            // inject workflow into form context + persist to sessionStorage
            // ✅ Now awaits the async cloneWorkflow (which includes date range API call)
            await cloneWorkflow(elem);

            // close modal, jump back to the first stage, and make sure we are
            // on the create-workflow page so the cloned data renders
            if (typeof onClose === "function") onClose();
            if (typeof setActiveTab === "function") setActiveTab("Stage1");
            navigate("/createworkflow");
        } catch (error) {
            console.error("Error cloning workflow:", error);
            // Optionally show error message to user
        }
    };


    if (!isOpen) return null;


    return (
        <div
            className={`fixed inset-0 z-[300] flex items-center justify-center px-4 transition-opacity ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                }`}
            style={{
                background: "rgba(3,43,78,0.55)",
                backdropFilter: "blur(4px)",
            }}
            onClick={onClose}
        >
            <div
                className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[1200px] max-h-[85vh] flex flex-col overflow-hidden"
                style={{ border: "1px solid #e6eef4", fontFamily: "Poppins, sans-serif" }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-[#032B4E]">
                    <h2
                        className="text-xl font-semibold text-white"
                        style={{ fontFamily: "Poppins, sans-serif" }}
                    >
                        Clone Existing Forecast
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:text-gray-200 text-2xl px-2"
                        aria-label="Close workflows modal"
                    >
                        ×
                    </button>
                </div>


                {/* Table */}
                <div className="p-4 flex-1 overflow-auto">
                    <div className="rounded-xl border border-[#e6eef4] overflow-hidden">
                        <table
                            className="w-full text-gray-800 border-collapse"
                            style={{ borderSpacing: 0, fontFamily: "Poppins, sans-serif" }}
                        >
                            <thead
                                className="bg-[#032B4E] text-white"
                                style={{
                                    fontFamily: "Poppins, sans-serif",
                                    fontWeight: 600,
                                    fontSize: "15px",
                                }}
                            >
                                <tr>
                                    <th className="px-6 py-4 text-center" style={{ borderRight: "2px solid rgba(255,255,255,0.1)" }}>WORKFLOW NAME</th>
                                    <th className="px-6 py-4 text-center" style={{ borderRight: "2px solid rgba(255,255,255,0.1)" }}>WORKFLOW DESCRIPTION</th>
                                    <th className="px-6 py-4 text-center" style={{ borderRight: "2px solid rgba(255,255,255,0.1)" }}>GRANULARITY</th>
                                    <th className="px-6 py-4 text-center" style={{ borderRight: "2px solid rgba(255,255,255,0.1)" }}>PROCESS TIME</th>
                                    <th className="px-6 py-4 text-center" style={{ borderRight: "2px solid rgba(255,255,255,0.1)" }}>PROCESS DATE</th>
                                    <th className="px-6 py-4 text-center" style={{ borderRight: "2px solid rgba(255,255,255,0.1)" }}>MODEL STATUS</th>
                                    <th className="px-6 py-4 text-center" style={{ borderRight: "2px solid rgba(255,255,255,0.1)" }}>APPROVAL STATUS</th>
                                    <th className="px-6 py-4 text-center">Clone Forecast</th>
                                </tr>
                            </thead>
                            <tbody
                                className="bg-white"
                                style={{ fontFamily: "Poppins, sans-serif", fontWeight: 400, fontSize: "13px" }}
                            >
                                {pageRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="text-center py-6 text-gray-500">
                                            No workflows found.
                                        </td>
                                    </tr>
                                ) : (
                                    pageRows.map((elem, index) => (
                                        <tr
                                            key={index}
                                            className="hover:bg-gray-50 transition-colors duration-200"
                                            style={{
                                                borderBottom:
                                                    index < pageRows.length - 1 ? "1px solid #e0e0e0" : "none",
                                            }}
                                        >
                                            <td
                                                className="px-6 py-4 text-center text-gray-900"
                                                style={{
                                                    fontWeight: 600,
                                                    borderRight: "1px solid #e0e0e0",
                                                    fontSize: "13px",
                                                }}
                                            >
                                                {elem.workflow_name}
                                            </td>
                                            <td
                                                className="px-6 py-4 text-center text-gray-700"
                                                style={{
                                                    fontWeight: 500,
                                                    borderRight: "1px solid #e0e0e0",
                                                    fontSize: "13px",
                                                }}
                                            >
                                                {elem.workflow_description}
                                            </td>
                                            <td
                                                className="px-6 py-4 text-center text-gray-700"
                                                style={{
                                                    fontWeight: 500,
                                                    borderRight: "1px solid #e0e0e0",
                                                    fontSize: "13px",
                                                }}
                                            >
                                                {elem.granularity}
                                            </td>
                                            <td
                                                className="px-6 py-4 text-center text-gray-700"
                                                style={{
                                                    fontWeight: 500,
                                                    borderRight: "1px solid #e0e0e0",
                                                    fontSize: "13px",
                                                }}
                                            >
                                                {elem.process_date && elem.process_date.split("T")[1].slice(0, 5)}
                                            </td>
                                            <td
                                                className="px-6 py-4 text-center text-gray-700"
                                                style={{
                                                    fontWeight: 500,
                                                    borderRight: "1px solid #e0e0e0",
                                                    fontSize: "13px",
                                                }}
                                            >
                                                {elem.process_date?.split("T")[0]}
                                            </td>
                                            <td
                                                className="px-6 py-4 text-center"
                                                style={{ borderRight: "1px solid #e0e0e0", fontSize: "13px" }}
                                            >
                                                <span
                                                    className="px-5 py-2 rounded-full font-bold uppercase tracking-wide inline-block"
                                                    style={{
                                                        minWidth: "110px",
                                                        fontSize: "12px",
                                                        backgroundColor:
                                                            elem.status?.toLowerCase() === "completed"
                                                                ? "#368E60"
                                                                : elem.status?.toLowerCase() === "processing"
                                                                    ? "#ca8b48ff"
                                                                    : "#f97316",
                                                        color: "#ffffff",
                                                    }}
                                                >
                                                    {elem.status}
                                                </span>
                                            </td>
                                            <td
                                                className="px-6 py-4 text-center"
                                                style={{ borderRight: "1px solid #e0e0e0", fontSize: "13px" }}
                                            >
                                                <span
                                                    className="px-5 py-2 rounded-full font-bold uppercase tracking-wide inline-block"
                                                    style={{
                                                        minWidth: "110px",
                                                        fontSize: "12px",
                                                        backgroundColor:
                                                            elem.approval_status?.toLowerCase() === "approved"
                                                                ? "#368E60"
                                                                : "#6a7d8c",
                                                        color: "#ffffff",
                                                    }}
                                                >
                                                    {elem.approval_status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-center" style={{ fontSize: "13px" }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleClone(elem)}
                                                    className="inline-flex items-center justify-center gap-1.5 transition-all hover:-translate-y-0.5"
                                                    style={{
                                                        padding: "8px 18px",
                                                        fontSize: 13,
                                                        fontWeight: 600,
                                                        fontFamily: "Poppins, sans-serif",
                                                        color: "#fff",
                                                        background: "#E06639",
                                                        border: "none",
                                                        borderRadius: 8,
                                                        cursor: "pointer",
                                                        boxShadow: "0 3px 10px rgba(224, 102, 57, 0.25)",
                                                    }}
                                                    aria-label={`Clone workflow ${elem.workflow_name}`}
                                                >
                                                    <iconify-icon icon="mdi:content-copy" width="16" height="16" />
                                                    Clone
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>


                {/* Pagination */}
                <div
                    className="flex justify-center items-center gap-3 py-4 border-t border-gray-200 bg-white"
                    style={{ fontFamily: "Poppins, sans-serif" }}
                >
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 border border-[#1F4280] rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#E06639] hover:text-white transition"
                    >
                        Previous
                    </button>


                    <span className="text-sm font-medium text-gray-700">
                        Page {currentPage} of {totalPages || 1}
                    </span>


                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 border border-[#1F4280] rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#E06639] hover:text-white transition"
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};


export default WorkflowTableModal;
